import {
  ConsultantSearchCardDataFragment,
  GetConsultantsQueryVariables,
  useConsultantExportBulkActionMutation,
  useConsultantExportSsnBulkActionMutation,
  useGetConsultantsLazyQuery,
  useGetConsultantsTotalLazyQuery
} from "@bonliva-crm/graphql";
import {
  BulkActionCommunicationSubType,
  BulkActionSubType,
  BulkActionType,
  ConsultantTableColumns,
  GDPRConsentType,
  GDPROptionType,
  SelectableBulkActionType
} from "@bonliva-crm/shared";
import {
  CallLogIcon,
  ChatBubbleIcon,
  Color,
  DeleteIcon,
  DraftIcon,
  FaceIcon,
  MailIcon,
  VerifiedIcon,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { BulkActionModal } from "@common/bulk-action/bulk-action-modal";
import { ExternalActionStepProps } from "@common/bulk-action/utils/action-step-props";
import { useGlobalStore, useLocale } from "@hooks";
import {
  Box,
  Divider,
  IconButton,
  makeStyles,
  MenuItem,
  Popover,
  Tooltip,
  Typography
} from "@material-ui/core";
import { CONSULTANT_SLIDER, ConsultantProfileLocation } from "@navigation";
import {
  IRowNode,
  RowClassParams,
  RowDoubleClickedEvent,
  RowStyle
} from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useInitialColumnDefs } from "../consultant-table/use-initial-column-defs";
import {
  buildConsentTooltip,
  findJobMailingConsent,
  resolveConsentState
} from "../consultant-table/renderers/gdpr-job-mailing-consent";
import { LocalOffer } from "@material-ui/icons";
import { SortOption } from "../../utils/consultant-filter-state";

type Props = {
  filters: GetConsultantsQueryVariables["filter"];
  sort: SortOption;
};

export const ConsultantBulkActions = (props: Props) => {
  const classes = useStyles();
  const { locale } = useLocale();
  const [bulkActionState, setBulkActionState] = useState<{
    open: boolean;
    type: SelectableBulkActionType | null;
    subType: (typeof BulkActionSubType)[SelectableBulkActionType] | null;
  }>({
    open: false,
    type: null,
    subType: null
  });

  const [loading, setLoading] = useState(false);
  const snack = useSnackbarsPipeline();
  const [consultants, setConsultants] = useState<ConsultantSearchCardDataFragment[]>([]);
  const { navigation, user, routing } = useGlobalStore();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [getConsultants] = useGetConsultantsLazyQuery({
    onError(error) {
      console.error(error);
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only"
  });

  const [consultantExportMutation, consultantExportData] =
    useConsultantExportBulkActionMutation({
      variables: {
        filter: props.filters
      },
      onError(error) {
        console.error(error);
        snack.showError(locale.ErrorOccurred);
      },
      onCompleted() {
        snack.showSuccess(locale.consultantExportSuccess);
      },
      fetchPolicy: "network-only"
    });

  const [consultantExportSsnMutation, consultantExportSsnData] =
    useConsultantExportSsnBulkActionMutation({
      variables: {
        filter: props.filters
      },
      onError(error) {
        console.error(error);
        snack.showError(locale.ErrorOccurred);
      },
      onCompleted() {
        snack.showSuccess(locale.consultantExportSsnSuccess);
      },
      fetchPolicy: "network-only"
    });

  const getGdprValues = () => {
    const defaultOptions = [GDPROptionType.Yes, GDPROptionType.NotSpecified];
    // For Behovsutskick we deliberately also pull in consultants who opted out
    // (GDPROptionType.No) so they can be shown greyed-out / non-selectable with a
    // reason, instead of silently disappearing from the pool. The send itself is
    // still gated server-side by EmailConsentService / SMSCommunication.
    const jobMailingDefaultOptions = [
      GDPROptionType.Yes,
      GDPROptionType.No,
      GDPROptionType.NotSpecified
    ];

    if (bulkActionState.subType === BulkActionCommunicationSubType.Marketing) {
      return {
        gdprMarketing: props.filters.gdprMarketing?.length
          ? props.filters.gdprMarketing
          : defaultOptions,
        gdprJobMailingEmail: props.filters.gdprJobMailingEmail,
        gdprJobMailingSms: props.filters.gdprJobMailingSms
      };
    }
    if (bulkActionState.subType === BulkActionCommunicationSubType.JobMailing) {
      return {
        gdprMarketing: props.filters.gdprMarketing,
        gdprJobMailingEmail: props.filters.gdprJobMailingEmail?.length
          ? props.filters.gdprJobMailingEmail
          : jobMailingDefaultOptions,
        gdprJobMailingSms: props.filters.gdprJobMailingSms?.length
          ? props.filters.gdprJobMailingSms
          : jobMailingDefaultOptions
      };
    }
    return {
      gdprMarketing: props.filters.gdprMarketing,
      gdprJobMailingEmail: props.filters.gdprJobMailingEmail,
      gdprJobMailingSms: props.filters.gdprJobMailingSms
    };
  };

  const { gdprMarketing, gdprJobMailingEmail, gdprJobMailingSms } = getGdprValues();

  const fitness =
    bulkActionState.type === BulkActionType.ConsultantSMS ||
    bulkActionState.type === BulkActionType.ConsultantEmail
      ? "fit"
      : props.filters.fitness;

  const filterValues = {
    ...props.filters,
    gdprMarketing,
    gdprJobMailingEmail,
    gdprJobMailingSms,
    fitness
  };

  const getConsultantsBatched = async (total: number) => {
    const batchSize = 2000;
    const limit = total > batchSize ? batchSize : total;
    const numberOfBatches = Math.ceil(total / batchSize);

    const consultantsBatched: ConsultantSearchCardDataFragment[] = [];

    for (let i = 0; i < numberOfBatches; i++) {
      const amountOfFetchedItems = batchSize * i;
      const hasFullResult = await getConsultants({
        variables: {
          limit: limit,
          skip: amountOfFetchedItems,
          withEvents: false,
          withCallListStats: false,
          filter: filterValues,
          filterText: "",
          sortBy: props.sort?.sortBy,
          sortOrder: props.sort?.sortOrder
        }
      })
        .then((res) => {
          consultantsBatched.push(...res.data.consultants.items);
        })
        .catch((err) => {
          console.error(err);
          snack.showError(locale.ErrorOccurred);
          return [];
        });
      if (hasFullResult) break;
    }
    return consultantsBatched;
  };

  const [getConsultantsTotal] = useGetConsultantsTotalLazyQuery({
    variables: {
      filter: filterValues
    },
    onError(error) {
      console.error(error);
    },
    fetchPolicy: "network-only"
  });

  useEffect(() => {
    if (bulkActionState.open) {
      setLoading(true);

      getConsultantsTotal()
        .then((res) =>
          getConsultantsBatched(res.data.consultantsTotal.total).then((consultants) =>
            setConsultants(consultants)
          )
        )
        .catch((e) => {
          console.error(e);
          snack.showError(locale.ErrorOccurred);
        })
        .finally(() => setLoading(false));
    }
  }, [bulkActionState.open]);

  // For Behovsutskick (the JobMailing sub-type) the channel-specific consent that
  // gates the send. Other sub-types (Information/Marketing) are left untouched.
  const isBehovsutskick =
    bulkActionState.subType === BulkActionCommunicationSubType.JobMailing;
  const channelConsentType = !isBehovsutskick
    ? null
    : bulkActionState.type === BulkActionType.ConsultantEmail
    ? GDPRConsentType.JobMailingEmail
    : bulkActionState.type === BulkActionType.ConsultantSMS
    ? GDPRConsentType.JobMailingSms
    : null;

  // Returns the revoked consent record for the active channel, or undefined when
  // the consultant has not opted out (granted or not specified — both allowed).
  const getOptOutConsent = useCallback(
    (consultant?: ConsultantSearchCardDataFragment | null) => {
      if (!channelConsentType || !consultant) {
        return undefined;
      }
      const consent = findJobMailingConsent(
        consultant.GDPRConsentRecords,
        channelConsentType
      );
      return resolveConsentState(consent) === "revoked" ? consent : undefined;
    },
    [channelConsentType]
  );

  const baseColDefs = useInitialColumnDefs(false);

  // For Behovsutskick, explain why an opted-out consultant cannot be selected via a
  // tooltip on the consultant's name (the row is also dimmed and non-selectable),
  // instead of adding a dedicated column.
  const colDefs = useMemo(() => {
    if (!channelConsentType) {
      return baseColDefs;
    }

    return baseColDefs.map((colDef) => {
      if (colDef.field !== ConsultantTableColumns.Name) {
        return colDef;
      }

      const renderName = colDef.cellRenderer as
        | ((p: CustomCellRendererProps<ConsultantSearchCardDataFragment>) => ReactNode)
        | undefined;

      return {
        ...colDef,
        cellRenderer: (p: CustomCellRendererProps<ConsultantSearchCardDataFragment>) => {
          const content = renderName ? renderName(p) : p.value;
          const consent = getOptOutConsent(p.data);
          if (!consent) {
            return content;
          }
          return (
            <Tooltip
              title={`${locale.optedOutOfJobMailing} — ${buildConsentTooltip(consent)}`}
            >
              <span>{content}</span>
            </Tooltip>
          );
        }
      };
    });
  }, [baseColDefs, channelConsentType, getOptOutConsent, locale]);

  const getRowStyle = useCallback(
    (
      params: RowClassParams<ConsultantSearchCardDataFragment>
    ): RowStyle | undefined => {
      return getOptOutConsent(params.data) ? { opacity: 0.5 } : undefined;
    },
    [getOptOutConsent]
  );

  const defaultColumns = useMemo(() => {
    return Object.values(ConsultantTableColumns).map((value) => ({
      label: locale.consultantTableColumnType[value],
      value: value
    }));
  }, []);

  const onRowClicked = useCallback(
    (event: RowDoubleClickedEvent<ConsultantSearchCardDataFragment, any>) => {
      return navigation.open(
        CONSULTANT_SLIDER,
        new ConsultantProfileLocation(event.data._id)
      );
    },
    []
  );

  const valueParsers: ExternalActionStepProps<ConsultantSearchCardDataFragment>["valueParsers"] =
    useMemo(() => {
      return {
        [BulkActionType.ConsultantEmail]: (rows) => {
          const parsed: {
            recipients: { email: string; name: string }[];
            nodeIds: string[];
          } = { recipients: [], nodeIds: [] };

          rows.forEach((row) => {
            if (row.Email && !parsed.recipients.some((r) => r.email === row.Email)) {
              parsed.recipients.push({ email: row.Email, name: row.Name });
              parsed.nodeIds.push(row._id);
            }
          });

          return parsed;
        },
        [BulkActionType.ConsultantSMS]: (rows) => {
          const parsed: {
            recipients: { phone: string; name: string }[];
            nodeIds: string[];
          } = { recipients: [], nodeIds: [] };

          rows.forEach((row) => {
            if (
              row.CellPhone &&
              !parsed.recipients.some((r) => r.phone === row.CellPhone)
            ) {
              parsed.recipients.push({ phone: row.CellPhone, name: row.Name });
              parsed.nodeIds.push(row._id);
            }
          });

          return parsed;
        },
        [BulkActionType.ConsultantReassignBA]: (rows) => {
          return { nodeIds: rows.map((row) => row._id) };
        },
        [BulkActionType.ConsultantAssignCallList]: (rows) => {
          return { nodeIds: rows.map((row) => row._id) };
        },
        [BulkActionType.ConsultantAssignTags]: (rows) => {
          return { nodeIds: rows.map((row) => row._id) };
        },
        [BulkActionType.ConsultantDelete]: (rows) => {
          return {
            nodeIds: rows.filter((row) => !row.ResponsiblePerson).map((row) => row._id)
          };
        },
        [BulkActionType.ConsultantOrderQualityDocuments]: (rows) => {
          return { nodeIds: rows.map((row) => row._id) };
        }
      };
    }, []);

  const onMenuItemClick = useCallback(
    (subType: BulkActionSubType) => {
      setAnchorEl(null);
      setBulkActionState({
        ...bulkActionState,
        open: true,
        subType: subType
      });
    },
    [bulkActionState]
  );

  const isRowSelectable = (params: IRowNode<ConsultantSearchCardDataFragment>) => {
    if (bulkActionState.type === BulkActionType.ConsultantDelete) {
      return !params.data.ResponsiblePerson;
    }

    // Consultants who opted out of Behovsutskick for the active channel are shown
    // but cannot be selected.
    if (getOptOutConsent(params.data)) {
      return false;
    }

    return true;
  };

  const canChangeBa = () => {
    if (user.can("consultant:update-ba-bulk:all")) {
      return true;
    }

    const allowedGroups = [
      "crm-group-oc-staffing",
      "crm-group-oc-support",
      "crm-team-juridikavdelning",
      "crm-group-hba"
    ];

    for (const group of allowedGroups) {
      if (user.myProfile.AzureGroups.includes(group)) {
        return true;
      }
    }

    return false;
  };

  return (
    <Box className={classes.root}>
      <Tooltip title={locale.callAndContactLists}>
        <IconButton
          className={classes.button}
          onClick={() => {
            setBulkActionState({
              open: true,
              type: BulkActionType.ConsultantAssignCallList,
              subType: null
            });
          }}
        >
          <CallLogIcon htmlColor={Color.Body} />
        </IconButton>
      </Tooltip>
      <Tooltip title={locale.SendSMS}>
        <IconButton
          className={classes.button}
          onClick={(e) => {
            setBulkActionState({
              ...bulkActionState,
              type: BulkActionType.ConsultantSMS
            });
            setAnchorEl(e.currentTarget);
          }}
        >
          <ChatBubbleIcon htmlColor={Color.Body} />
        </IconButton>
      </Tooltip>
      <Tooltip title={locale.SendEmail}>
        <IconButton
          className={classes.button}
          onClick={(e) => {
            setBulkActionState({
              ...bulkActionState,
              type: BulkActionType.ConsultantEmail
            });
            setAnchorEl(e.currentTarget);
          }}
        >
          <MailIcon htmlColor={Color.Body} />
        </IconButton>
      </Tooltip>
      {canChangeBa() && (
        <Tooltip title={locale.ChangeBA}>
          <IconButton
            className={classes.button}
            onClick={() => {
              setBulkActionState({
                open: true,
                type: BulkActionType.ConsultantReassignBA,
                subType: null
              });
            }}
          >
            <FaceIcon htmlColor={Color.Body} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={locale.addTags}>
        <IconButton
          className={classes.button}
          onClick={() => {
            setBulkActionState({
              open: true,
              type: BulkActionType.ConsultantAssignTags,
              subType: null
            });
          }}
        >
          <LocalOffer htmlColor={Color.Body} />
        </IconButton>
      </Tooltip>
      <Tooltip title={locale.bulkActionSteps.action.ConsultantOrderQualityDocuments}>
        <IconButton
          className={classes.button}
          onClick={() =>
            setBulkActionState({
              open: true,
              type: BulkActionType.ConsultantOrderQualityDocuments,
              subType: null
            })
          }
        >
          <VerifiedIcon htmlColor={Color.Body} />
        </IconButton>
      </Tooltip>
      <Tooltip title={locale.Delete}>
        <IconButton
          className={classes.button}
          onClick={() =>
            setBulkActionState({
              open: true,
              type: BulkActionType.ConsultantDelete,
              subType: null
            })
          }
        >
          <DeleteIcon htmlColor={Color.Body} />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {user.can("mass-actions:export:all") && (
        <>
          <Tooltip title={locale.exportToExcel}>
            <IconButton
              className={classes.button}
              onClick={() => consultantExportMutation()}
              disabled={consultantExportData.loading}
            >
              <DraftIcon htmlColor={Color.Body} />
              <Typography variant="subtitle1" className={classes.excelIconText}>
                X
              </Typography>
            </IconButton>
          </Tooltip>
          <Tooltip title="Importera från Excel">
            <IconButton
              className={classes.button}
              onClick={() => routing.push("/consultant_import")}
            >
              <DraftIcon htmlColor={Color.Body} />
              <Typography variant="subtitle1" className={classes.excelImportIconText}>
                {"↑"}
              </Typography>
            </IconButton>
          </Tooltip>
        </>
      )}
      {user.can("mass-actions:export-ssn:all") && (
        <Tooltip title={locale.exportToExcelWithSsn}>
          <IconButton
            className={classes.button}
            onClick={() => consultantExportSsnMutation()}
            disabled={consultantExportSsnData.loading}
          >
            <DraftIcon htmlColor={Color.Body} />
            <Typography variant="subtitle1" className={classes.excelSsnIconText}>
              P
            </Typography>
          </IconButton>
        </Tooltip>
      )}

      <BulkActionModal
        open={bulkActionState.open}
        setOpen={(open) => setBulkActionState({ ...bulkActionState, open })}
        type={bulkActionState.type}
        subType={bulkActionState.subType}
        stepProps={{
          Selection: {
            colDefs,
            rowData: consultants,
            isRowSelectable: isRowSelectable,
            defaultColumns: defaultColumns,
            isLoading: loading,
            onRowClicked: onRowClicked,
            getRowStyle: getRowStyle
          },
          Action: {
            valueParsers: valueParsers
          }
        }}
      />

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <div style={{ padding: "0.5rem 0" }}>
          <MenuItem
            onClick={() => onMenuItemClick(BulkActionCommunicationSubType.Information)}
          >
            {locale.bulkActionSubType[BulkActionCommunicationSubType.Information]}
          </MenuItem>
          <MenuItem
            onClick={() => onMenuItemClick(BulkActionCommunicationSubType.JobMailing)}
            disabled={
              props.filters.gdprJobMailingEmail?.includes(GDPROptionType.No) &&
              props.filters.gdprJobMailingSms?.includes(GDPROptionType.No)
            }
          >
            {locale.bulkActionSubType[BulkActionCommunicationSubType.JobMailing]}
          </MenuItem>
          <MenuItem
            onClick={() => onMenuItemClick(GDPRConsentType.Marketing)}
            disabled={props.filters.gdprMarketing?.includes(GDPROptionType.No)}
          >
            {locale.bulkActionSubType[BulkActionCommunicationSubType.Marketing]}
          </MenuItem>
        </div>
      </Popover>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1)
  },
  button: {
    border: `1px solid ${Color.LightGray}`,
    borderRadius: "0.375rem",
    padding: "8px 10px"
  },
  excelIconText: {
    color: Color.White,
    position: "absolute",
    top: "calc(50% - 3px)",
    left: "25%",
    border: `1px solid ${Color.White}`,
    borderRadius: "4px",
    backgroundColor: Color.Body,
    lineHeight: "11px",
    fontSize: "10px",
    padding: "0 2px"
  },
  excelImportIconText: {
    color: Color.White,
    position: "absolute",
    top: "calc(50% - 3px)",
    left: "25%",
    border: `1px solid ${Color.White}`,
    borderRadius: "4px",
    backgroundColor: Color.Body,
    lineHeight: "11px",
    fontSize: "10px",
    padding: "0 2px"
  },
  excelSsnIconText: {
    color: Color.White,
    position: "absolute",
    top: "calc(50% - 3px)",
    left: "25%",
    border: `1px solid ${Color.White}`,
    borderRadius: "4px",
    backgroundColor: Color.Body,
    lineHeight: "11px",
    fontSize: "10px",
    padding: "0 2px"
  }
}));
